import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { HowTo as HowToContent } from '../content/how-to';

/**
 * THE ⓘ — "how this works", in a popup window, never on the screen until asked for.
 *
 * Owner, 2026-10-04: a popup, not a panel that opens in the page and pushes everything down. A
 * native <dialog> opened modally: the page dims behind it, focus moves into it and back to the ⓘ
 * afterwards, and Escape, the ✕ or a tap outside closes it — the same on a phone and a computer.
 * The wording lives in `content/how-to.ts`.
 *
 * `useHowTo` hands back the button and the popup separately, so a screen can put the ⓘ beside its
 * title; the popup renders wherever `panel` is placed, but always appears centred over the page.
 */
export function useHowTo(content: HowToContent, label?: string): { button: ReactNode; panel: ReactNode } {
  const [open, setOpen] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const node = dialog.current;
    if (!node) return;
    if (open && !node.open) node.showModal();
    if (!open && node.open) node.close();
  }, [open]);

  const button = (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-label={label ? undefined : content.title}
      onClick={() => setOpen(true)}
      className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-full text-ink-2 hover:text-ink"
    >
      <span
        aria-hidden="true"
        className="flex size-6 items-center justify-center rounded-full border border-stroke font-display text-[14px] italic"
      >
        i
      </span>
      {label ? <span className="text-[14px] underline underline-offset-4">{label}</span> : null}
    </button>
  );

  const panel = (
    <dialog
      ref={dialog}
      aria-label={content.title}
      onClose={() => setOpen(false)}
      /* A tap on the dimmed backdrop lands on the dialog element itself, not on its content. */
      onClick={(event) => {
        if (event.target === event.currentTarget) setOpen(false);
      }}
      className="m-auto w-[min(92vw,30rem)] rounded-lg border border-light bg-paper p-0 text-ink shadow-lg backdrop:bg-deep/40"
    >
      <div className="px-5 pb-5 pt-4">
        <div className="flex items-start justify-between gap-3">
          <h2 className="pt-1 font-display text-[20px] leading-tight">{content.title}</h2>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="-mr-2 flex min-h-[44px] min-w-[44px] items-center justify-center text-[20px] text-ink-2 hover:text-ink"
          >
            ✕
          </button>
        </div>
        <ul className="mt-2 flex list-disc flex-col gap-2 pl-5 text-[15px] leading-snug text-ink-2">
          {content.lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>
    </dialog>
  );

  return { button, panel };
}
